/**
 * voice-recognition.ts — Browser Web Speech API wrapper.
 *
 * Encapsulates SpeechRecognition setup (including the webkit- prefix needed
 * by older Edge/Chrome builds) behind a simple start/stop interface with
 * callbacks for interim & final results.
 *
 * Phase 2 will add a Whisper-API backend fallback; this module is the
 * client-only Phase 1 implementation that costs $0.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

// The Web Speech API types are not available in all TS lib configurations.
// We use `any` for the recognition instance and event types.
type SpeechRecognitionInstance = any;

export interface VoiceRecognitionCallbacks {
  /** Called repeatedly while the user is still speaking. */
  onInterimResult: (text: string) => void;
  /** Called when a final, confirmed transcript segment is available. */
  onFinalResult: (text: string) => void;
  /** Called on any recognition error (permission denied, network, etc.). */
  onError: (error: string) => void;
  /** Called when recognition ends (user-initiated or automatic). */
  onEnd: () => void;
}

export interface VoiceRecognitionHandle {
  start: () => Promise<void>;
  stop: () => void;
  /** Whether the Web Speech API is available in the current browser. */
  isSupported: boolean;
}

/**
 * Detect browser SpeechRecognition constructor.
 * Edge WebView2 (the Excel add-in host) exposes the webkit-prefixed version.
 */
function getSpeechRecognitionCtor(): (new () => SpeechRecognitionInstance) | null {
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Request microphone permission explicitly via getUserMedia.
 *
 * Office Add-in WebView2 does NOT show the standard browser permission prompt
 * when SpeechRecognition.start() is called. By calling getUserMedia first, we
 * force the OS-level / browser-level microphone permission dialog to appear.
 * The MediaStream is immediately stopped — we only need the side effect of
 * granting the "microphone" permission.
 */
async function ensureMicPermission(): Promise<void> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  // Permission granted — stop all tracks immediately, we don't need the stream.
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

/**
 * Create a voice recognition handle.
 *
 * @param lang  BCP-47 language tag, e.g. `"vi-VN"` or `"en-US"`.
 * @param cbs   Callback bundle.
 */
export function createVoiceRecognition(
  lang: string,
  cbs: VoiceRecognitionCallbacks,
): VoiceRecognitionHandle {
  const Ctor = getSpeechRecognitionCtor();

  if (!Ctor) {
    return {
      isSupported: false,
      start: async () => cbs.onError("Voice recognition is not supported in this browser."),
      stop: () => {},
    };
  }

  let recognition: SpeechRecognitionInstance | null = null;
  /** Tracks whether stop() was called explicitly (vs. auto-end). */
  let manualStop = false;
  /** Whether mic permission has been granted at least once this session. */
  let permissionGranted = false;

  async function start() {
    // ── Step 1: Ensure microphone permission ─────────────────────────
    // WebView2 in Office add-ins silently blocks SpeechRecognition
    // without showing a permission prompt. getUserMedia forces the dialog.
    if (!permissionGranted) {
      try {
        await ensureMicPermission();
        permissionGranted = true;
      } catch (err: any) {
        // Map getUserMedia errors to our error codes.
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          cbs.onError("VOICE_ERROR_PERMISSION");
        } else if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
          cbs.onError("VOICE_ERROR_MIC");
        } else {
          cbs.onError("VOICE_ERROR_GENERIC");
        }
        cbs.onEnd();
        return;
      }
    }

    // ── Step 2: Start SpeechRecognition ──────────────────────────────
    // Tear down any previous instance to avoid ghost listeners.
    if (recognition) {
      try { recognition.abort(); } catch { /* noop */ }
    }

    recognition = new Ctor!();
    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;
    manualStop = false;

    recognition.onresult = (event: any) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      if (final) cbs.onFinalResult(final);
      if (interim) cbs.onInterimResult(interim);
    };

    recognition.onerror = (event: any) => {
      const mapped = mapError(event.error);
      // "no-speech" and "aborted" are non-fatal — don't surface to user.
      if (event.error !== "no-speech" && event.error !== "aborted") {
        cbs.onError(mapped);
      }
    };

    recognition.onend = () => {
      // When continuous=true, Chrome may auto-stop after silence.
      // Restart unless the caller explicitly called stop().
      if (!manualStop && recognition) {
        try {
          recognition.start();
          return; // don't fire onEnd; we're re-starting.
        } catch {
          // If restart fails, fall through to onEnd.
        }
      }
      cbs.onEnd();
    };

    try {
      recognition.start();
    } catch (err: any) {
      cbs.onError(err.message ?? "Failed to start voice recognition.");
    }
  }

  function stop() {
    manualStop = true;
    if (recognition) {
      try { recognition.stop(); } catch { /* noop */ }
    }
  }

  return { start, stop, isSupported: true };
}

/** Map the SpeechRecognitionErrorEvent.error code to a human-friendly string. */
function mapError(code: string): string {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "VOICE_ERROR_PERMISSION";
    case "network":
      return "VOICE_ERROR_NETWORK";
    case "audio-capture":
      return "VOICE_ERROR_MIC";
    default:
      return "VOICE_ERROR_GENERIC";
  }
}
